import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { WidgetHeader } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';
import { fetchBillingPage, fetchPaymentAvailability } from '@features/billing/api';
import { BalanceCard } from '@features/billing/Invoices';
import { useModuleStore } from '@shared/stores/moduleStore';

/**
 * Home widget: the member's account balance card (reuses the billing
 * BalanceCard). Self-gates on the financials module + an existing account.
 */
export function AccountWidget({ config }: WidgetProps): JSX.Element | null {
  const navigate = useNavigate();
  const financialsEnabled = useModuleStore((s) => s.isModuleEnabled('financials'));

  // Same query keys/params as the Invoices page so the cache is shared (deduped).
  const billingQuery = useQuery({
    queryKey: ['billing', 'all'],
    queryFn: () => fetchBillingPage({ type: 'all', limit: 50 }),
    enabled: financialsEnabled,
  });
  const payQuery = useQuery({
    queryKey: ['payment-availability'],
    queryFn: fetchPaymentAvailability,
    enabled: financialsEnabled,
  });

  if (!financialsEnabled) return null;

  const account = billingQuery.data?.account;
  // No billing account linked → hide the widget entirely.
  if (!billingQuery.isLoading && !account) return null;

  return (
    <section>
      <WidgetHeader
        title={widgetTitle(config)}
        actionLabel="View"
        onAction={() => navigate('/app/invoices')}
      />
      {billingQuery.isLoading || !account ? (
        <Skeleton className="h-40 w-full rounded-2xl" />
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate('/app/invoices')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/app/invoices');
            }
          }}
          className="cursor-pointer rounded-2xl transition-transform active:scale-[0.99]"
        >
          <BalanceCard account={account} canPay={payQuery.data === true} />
        </div>
      )}
    </section>
  );
}
